import React, { useEffect, useRef, useState } from 'react';
import { X, Camera, Image as ImageIcon, RotateCcw } from 'lucide-react';
import { scanNutritionLabelPhoto, confirmPhotoScanFood, getNutritionFoods } from '../api';
import { pairedName, SourceTag } from './FoodPicker';

// Fallback for an unrecognised barcode (Open Food Facts had no match): the player photographs
// the nutrition-facts table on the package, Claude vision reads it, and the player checks/
// corrects the values before anything is written to the shared food database. Modal, same shape
// as BarcodeScanner. Calls onSaved(food) once the player confirms — food is a normal
// nutrition_foods row (source='photo') the caller treats exactly like a barcode match.
//
// Reused as-is for the general "add a new product" flow (AddFoodModal.js, any role): `barcode`
// is optional there (anyone can add a product with no barcode at all, not just the unrecognised-
// barcode fallback), and `confirmFn`/`title`/`introText` let that caller point the save step at
// the shared confirm endpoint and adjust the copy, without duplicating this whole capture/vision/
// confirm flow a second time.
const MAX_DIM = 1280;
const JPEG_QUALITY = 0.82;

// Resize/compress client-side before sending — a raw phone-camera photo can be several MB;
// nothing about reading a printed label needs that resolution, and it slows the upload for no
// benefit. Returns a "data:image/jpeg;base64,..." string.
function compressImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('could not read file'));
    reader.onload = () => {
      const img = new window.Image();
      img.onerror = () => reject(new Error('could not decode image'));
      img.onload = () => {
        const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', JPEG_QUALITY));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

const FIELDS_REQUIRED = [
  ['kcal_per_100g', 'kcal / 100g'],
  ['protein_g_per_100g', 'protein g / 100g'],
  ['carbs_g_per_100g', 'carbs g / 100g'],
  ['fat_g_per_100g', 'fat g / 100g'],
];
const FIELDS_OPTIONAL = [
  ['saturated_fat_g_per_100g', 'saturated fat g / 100g'],
  ['sugars_g_per_100g', 'sugars g / 100g'],
  ['fiber_g_per_100g', 'fiber g / 100g'],
  ['salt_g_per_100g', 'salt g / 100g'],
];

const CARD = { background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 16, width: '100%', maxWidth: 400, maxHeight: '90vh', overflowY: 'auto' };
const inputStyle = { padding: '7px 9px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', outline: 'none' };
const btn = (active, color = '#3b82f6') => ({
  padding: '8px 14px', borderRadius: 8, border: `1px solid ${active ? color : 'var(--border-strong)'}`,
  background: active ? color : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
  fontSize: 12, fontWeight: active ? 600 : 400, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center',
});
const label = { fontSize: 11, color: 'var(--text-muted)', marginBottom: 3, display: 'block' };
const banner = (tone) => ({
  fontSize: 12, padding: '8px 10px', borderRadius: 8, marginBottom: 12,
  background: tone === 'warn' ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.12)',
  color: tone === 'warn' ? '#f59e0b' : '#ef4444',
});

export default function NutritionLabelPhotoScan({
  barcode, onSaved, onClose, confirmFn = confirmPhotoScanFood,
  title = 'Unrecognised product',
  introText = barcode
    ? "This barcode isn't in our database yet. Take a photo of the nutrition-facts table on the package and we'll read it for you."
    : 'Take a photo of the nutrition-facts table on the package and we\'ll read it for you.',
  // Default 'en' matches every other string in this component, which was built English-first for
  // the player flow; AddFoodModal.js (chef, Dutch) passes lang="nl" explicitly when it renders
  // this for a chef/admin session — see that component for why.
  lang = 'en',
}) {
  const [step, setStep] = useState('capture'); // capture | analyzing | confirm | saving
  const [notice, setNotice] = useState(null);   // { tone: 'warn'|'error', text }
  const [saveErr, setSaveErr] = useState('');
  const [form, setForm] = useState(null);       // set when entering 'confirm'
  const [similar, setSimilar] = useState([]);   // possible existing matches for the typed name
  const fileInputCamera = useRef(null);
  const fileInputLibrary = useRef(null);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Live duplicate nudge: as the name is typed/read, check whether something close already
  // exists in the shared database — two people adding the same product under a slightly
  // different spelling is the main way this would otherwise silently duplicate. Never blocks
  // saving; picking a suggestion just adopts that existing food instead (skips save() entirely).
  const formName = form ? form.name : '';
  useEffect(() => {
    if (step !== 'confirm' || !formName.trim() || formName.trim().length < 3) { setSimilar([]); return; }
    const t = setTimeout(async () => {
      try { setSimilar((await getNutritionFoods(formName.trim())).slice(0, 4) || []); }
      catch { setSimilar([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [step, formName]);

  const startManual = () => {
    setNotice(null);
    setForm({ name: '', kcal_per_100g: '', protein_g_per_100g: '', carbs_g_per_100g: '', fat_g_per_100g: '',
      saturated_fat_g_per_100g: '', sugars_g_per_100g: '', fiber_g_per_100g: '', salt_g_per_100g: '', grams_per_portion: '' });
    setStep('confirm');
  };

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // allow picking the same file again after a retake
    if (!file) return;
    setStep('analyzing'); setNotice(null);
    try {
      const dataUrl = await compressImage(file);
      const r = await scanNutritionLabelPhoto(dataUrl);
      if (!r.ok) {
        setNotice({ tone: 'error', text: r.error === 'not_configured'
          ? 'Photo scan is not available right now — fill in the values manually.'
          : 'Could not read the photo — try again, or fill in the values manually.' });
        setStep('capture');
        return;
      }
      const p100 = r.per_100g || {};
      const num = (v) => (v == null ? '' : String(v));
      setForm({
        name: r.product_name || '',
        kcal_per_100g: num(p100.kcal), protein_g_per_100g: num(p100.protein_g),
        carbs_g_per_100g: num(p100.carbs_g), fat_g_per_100g: num(p100.fat_g),
        saturated_fat_g_per_100g: num(p100.saturated_fat_g), sugars_g_per_100g: num(p100.sugars_g),
        fiber_g_per_100g: num(p100.fiber_g), salt_g_per_100g: num(p100.salt_g),
        grams_per_portion: num(r.portion_g),
      });
      if (!r.readable) {
        setNotice({ tone: 'warn', text: 'Could not find a nutrition table in that photo. Try again, or fill in the values manually.' });
      } else if (!r.complete) {
        setNotice({ tone: 'warn', text: 'Not everything on the label could be read — check and complete the values below.' });
      } else {
        setNotice(null);
      }
      setStep('confirm');
    } catch (err) {
      setNotice({ tone: 'error', text: 'Could not process that photo — try again, or fill in the values manually.' });
      setStep('capture');
    }
  };

  const retake = () => { setForm(null); setNotice(null); setStep('capture'); };

  const requiredOk = form && FIELDS_REQUIRED.every(([k]) => Number(form[k]) >= 0 && form[k] !== '') && form.name.trim();

  const save = async () => {
    if (!requiredOk) return;
    setStep('saving'); setSaveErr('');
    try {
      const payload = { name: form.name.trim() };
      if (barcode) payload.barcode = barcode;
      for (const [k] of [...FIELDS_REQUIRED, ...FIELDS_OPTIONAL]) {
        payload[k] = form[k] === '' || form[k] == null ? null : Number(form[k]);
      }
      payload.grams_per_portion = form.grams_per_portion === '' ? null : Number(form.grams_per_portion);
      const r = await confirmFn(payload);
      if (!r.ok || !r.food) {
        setSaveErr(r.error || 'Saving failed — try again.');
        setStep('confirm');
        return;
      }
      onSaved(r.food);
    } catch (err) {
      setSaveErr('Saving failed — try again.');
      setStep('confirm');
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={CARD}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-card)' }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}><X size={18} /></button>
        </div>

        <div style={{ padding: 16 }}>
          {step === 'capture' && (
            <>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 14 }}>
                {introText}
              </div>
              {notice && <div style={banner(notice.tone)}>{notice.text}</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button style={btn(true)} onClick={() => fileInputCamera.current.click()}>
                  <Camera size={14} /> Take a photo
                </button>
                <button style={btn(false)} onClick={() => fileInputLibrary.current.click()}>
                  <ImageIcon size={14} /> Choose from library
                </button>
                <button style={{ ...btn(false), border: 'none' }} onClick={startManual}>Fill in manually instead</button>
              </div>
              <input ref={fileInputCamera} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={onFile} />
              <input ref={fileInputLibrary} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFile} />
            </>
          )}

          {step === 'analyzing' && (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', padding: '30px 8px' }}>Reading the label…</div>
          )}

          {(step === 'confirm' || step === 'saving') && form && (
            <>
              {notice && <div style={banner(notice.tone)}>{notice.text}</div>}
              {saveErr && <div style={banner('error')}>{saveErr}</div>}

              <div style={{ marginBottom: 10 }}>
                <span style={label}>Product name</span>
                <input style={inputStyle} value={form.name} onChange={e => setField('name', e.target.value)} placeholder="e.g. Protein bar" />
              </div>

              {similar.length > 0 && (
                <div style={{ marginBottom: 10, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, padding: 8 }}>
                  <div style={{ fontSize: 11, color: '#f59e0b', marginBottom: 6 }}>
                    {lang === 'nl' ? 'Bedoel je een van deze bestaande producten?' : 'Did you mean one of these existing products?'}
                  </div>
                  {similar.map(f => (
                    <button key={f.id} type="button" onClick={() => onSaved(f)}
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '5px 6px', background: 'none', border: 'none', color: 'var(--text-primary)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', borderRadius: 6 }}>
                      {pairedName(f)}<SourceTag source={f.source} />
                      <span style={{ color: 'var(--text-dimmed)', marginLeft: 6, fontSize: 11 }}>{Math.round(f.kcal_per_100g)} kcal/100g</span>
                    </button>
                  ))}
                </div>
              )}

              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em', margin: '10px 0 6px' }}>Per 100 g — required</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 6 }}>
                {FIELDS_REQUIRED.map(([k, l]) => (
                  <div key={k}>
                    <span style={label}>{l}</span>
                    <input style={inputStyle} type="number" min="0" step="0.1" value={form[k]} onChange={e => setField(k, e.target.value)} />
                  </div>
                ))}
              </div>

              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em', margin: '10px 0 6px' }}>Per 100 g — if visible</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 6 }}>
                {FIELDS_OPTIONAL.map(([k, l]) => (
                  <div key={k}>
                    <span style={label}>{l}</span>
                    <input style={inputStyle} type="number" min="0" step="0.1" value={form[k]} onChange={e => setField(k, e.target.value)} />
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 10, marginBottom: 4 }}>
                <span style={label}>Portion size on the label (g, optional)</span>
                <input style={{ ...inputStyle, width: 140 }} type="number" min="0" step="1" value={form.grams_per_portion} onChange={e => setField('grams_per_portion', e.target.value)} />
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <button style={btn(true)} onClick={save} disabled={!requiredOk || step === 'saving'}>{step === 'saving' ? 'Saving…' : 'Save'}</button>
                <button style={btn(false)} onClick={retake} disabled={step === 'saving'}><RotateCcw size={12} /> Retake</button>
                <button style={{ ...btn(false), border: 'none' }} onClick={onClose} disabled={step === 'saving'}>Cancel</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
